import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  TextField
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { Dv360Icon } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const GOOGLE_GREEN = '#34A853';

// Shown after the Display & Video 360 Google sign-in: every advertiser under the partners the login can read. A login
// with advertiser-level access only may see none: it can type an advertiser ID, which the backend proves with
// advertisers.get before connecting. Each advertiser the user ticks becomes its own connector.
const Dv360Selection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [tempId, setTempId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [typedId, setTypedId] = useState('');
  const [partnerIsOurs, setPartnerIsOurs] = useState(false);  // explicit opt-in for partner-level settings

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }
    setTempId(temp_id);
    loadAccounts(temp_id);
  }, [searchParams]);

  const loadAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/dv360/accounts', { params: { temp_id } });
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
        setUserEmail(response.data.user_email || '');
      } else {
        setError(response.data.message || 'Failed to load Display & Video 360 advertisers');
      }
    } catch (err) {
      console.error('Error loading Display & Video 360 advertisers:', err);
      setError(err.response?.data?.message || 'Failed to load Display & Video 360 advertisers');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    if (connecting) return;
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  // An advertiser the partner listing does not show (advertiser-level access): added as typed, proven by the backend.
  const addTyped = () => {
    const id = typedId.trim();
    if (!/^\d+$/.test(id)) {
      setError('An advertiser ID is a number, as shown in Display & Video 360');
      return;
    }
    setError('');
    if (!accounts.some(a => a.id === id)) {
      setAccounts(prev => [...prev, { id, name: '', partner_name: '', typed: true }]);
    }
    setSelected(prev => (prev.includes(id) ? prev : [...prev, id]));
    setTypedId('');
  };

  const handleConnect = async () => {
    if (selected.length === 0) {
      setError('Please choose at least one advertiser to connect');
      return;
    }
    setConnecting(true);
    setError('');
    try {
      const response = await api.post('/api/connectors/dv360/select', {
        temp_id: tempId,
        advertiser_ids: selected,
        partner_is_ours: partnerIsOurs
      });
      if (response.data.success) {
        try {
          await api.post('/api/connectors/dv360/cleanup', { temp_id: tempId });
        } catch (cleanupError) {
          console.warn('Failed to cleanup temp data:', cleanupError);
        }
        navigate('/connectors', {
          state: { message: response.data.message || 'Display & Video 360 connected', severity: 'success' }
        });
      } else {
        // Partial success: keep the page so the failed accounts can be retried.
        const failedIds = (response.data.failures || []).map(f => f.advertiser_id);
        setSelected(failedIds);
        setError(response.data.message || 'Failed to connect the selected advertisers');
      }
    } catch (err) {
      console.error('Error connecting Display & Video 360 advertisers:', err);
      setError(err.response?.data?.message || 'Failed to connect the selected advertisers');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Display & Video 360 advertisers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/connectors')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Box sx={{ mr: 2, display: 'flex', flexShrink: 0 }}>
          <Dv360Icon size={32} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Choose Display & Video 360 Advertisers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userEmail ? `Signed in as ${userEmail}. ` : ''}Each advertiser you choose becomes its own connector.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select advertisers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The connector syncs the advertiser's campaigns, insertion orders, line items, ads, creatives, targeting and daily reports, in the advertiser's own currency and time zone.
          </Typography>

          {accounts.length === 0 ? (!error && (
            <Alert severity="warning">
              No partner of this Google login lists advertisers. If you have access to single advertisers, add them by advertiser ID below.
            </Alert>
          )) : (
            <Grid container spacing={2}>
              {accounts.map((account) => {
                const isSelected = selected.includes(account.id);
                return (
                  <Grid item xs={12} md={6} key={account.id}>
                    <Card
                      variant="outlined"
                      onClick={() => toggle(account.id)}
                      sx={{
                        height: '100%',
                        cursor: connecting ? 'default' : 'pointer',
                        borderColor: isSelected ? GOOGLE_GREEN : undefined,
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggle(account.id)}
                              disabled={connecting}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#fff', border: '1px solid', borderColor: 'divider', mr: 2 }}>
                                  <Dv360Icon size={22} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {account.name || account.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {account.id}{account.partner_name ? ` · ${account.partner_name}` : ''}
                                  </Typography>
                                  {account.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={account.connectedBrandName
                                          ? `Connected · ${account.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {account.currency && <Chip size="small" label={account.currency} />}
                                {account.time_zone && <Chip size="small" variant="outlined" label={account.time_zone} />}
                                {account.status && account.status !== 'ENTITY_STATUS_ACTIVE' && (
                                  <Chip size="small" color="warning" label={account.status.replace('ENTITY_STATUS_', '')} />
                                )}
                                {account.typed && <Chip size="small" variant="outlined" label="Added by ID" />}
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
            Add an advertiser by ID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use this when you have access to an advertiser but not to its partner. We check that this Google login can open it before connecting.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Advertiser ID"
              value={typedId}
              onChange={(e) => setTypedId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTyped(); }}
              disabled={connecting}
            />
            <Button variant="outlined" onClick={addTyped} disabled={connecting || !typedId.trim()}>
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>

      {accounts.length > 0 && (
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={partnerIsOurs}
              onChange={() => setPartnerIsOurs(v => !v)}
              disabled={connecting}
            />
          }
          label="The partner is my company's: also read partner-wide settings (not available if another company has advertisers under this partner)"
        />
      )}

      {accounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/connectors')} disabled={connecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={selected.length === 0 || connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ backgroundColor: GOOGLE_GREEN, '&:hover': { backgroundColor: '#1E8E3E' } }}
          >
            {connecting ? 'Connecting...' : `Connect ${selected.length || ''} Advertiser${selected.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Dv360Selection;
